import { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import cm from 'classnames';

import { useAppSelector } from 'store/store-hooks';
import { usePickerContext } from './picker.provider';

import { ReactComponent as AddFile } from 'assets/ui/add-file.svg';
import { ToastRejectedFiles } from './_toast-rejected-files';
import styles from './_picker.module.scss';

interface Props {
  children: React.ReactNode;
}

export const PickerDropZone = ({ children }: Props) => {
  const dragCounter = useRef(0);
  const extensions = useAppSelector((state) => state.app__data.extensions);
  const [active, setActive] = useState(false);
  const { onPick } = usePickerContext();

  const onDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragCounter.current += 1;
    setActive(true);
  };

  const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragCounter.current -= 1;
    if (dragCounter.current === 0) setActive(false);
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragCounter.current = 0;
    setActive(false);

    const files = Array.from(e.dataTransfer?.files || []);
    if (!extensions) return;

    const acceptedFiles: File[] = [];
    const rejectedFiles: File[] = [];
    const allowedExtensions = [...extensions.audio, ...extensions.video, ...extensions.image].map((ext) =>
      ext.toLowerCase()
    );

    for (const file of files) {
      const nameParts = file.name.toLowerCase().split('.');
      const ext = nameParts.length > 1 ? `.${nameParts.pop()}` : '';
      if (allowedExtensions.includes(ext)) acceptedFiles.push(file);
      else rejectedFiles.push(file);
    }

    if (acceptedFiles.length > 0) onPick(acceptedFiles);
    if (rejectedFiles.length > 0)
      toast.error(<ToastRejectedFiles rejected={rejectedFiles} extensions={extensions} />, { autoClose: false });
  };

  return (
    <div
      className={cm(styles.PickerDropZone, { [styles['PickerDropZone--active']]: active })}
      onDragOver={(e) => e.preventDefault()}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <div className={styles.PickerDropZone__overlay}>
        <AddFile />
        <p>Drop files here to add them to the project</p>
      </div>
      <div className={styles.PickerDropZone__inner}>{children}</div>
    </div>
  );
};
